<script setup lang="ts">
  import { useModal } from '@/composition/use-modal';
  import { IOrder } from '@/interfaces/IOrder';
  import { ITable } from '@/interfaces/ITable';
  import ModalPosNewOrder from '../new-order/ModalPosNewOrder.vue';
  import { computed, nextTick, ref } from 'vue';
  import { useUi } from '@/composition/use-ui';
  import { AlertType } from '@/interfaces/IAlert';
  import TablesService from '@/api/services/tables.service';
  import { useOrders } from '@/composition/use-orders';
  const props = defineProps<{
    table: ITable;
    orders: IOrder[];
    search?: string | null;
  }>();

  const emit = defineEmits(['update:search']);

  const ordersHelper = useOrders();
  const modals = useModal();
  const ui = useUi();

  function openNewOrderModal() {
    modals.show(
      ModalPosNewOrder,
      {
        table: props.table,
      },
      {
        name: 'pos-new-order',
        adaptive: true,
        classes: 'dialog--height-limit',
        height: 800,
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        width: 1280,
        transition: 'none',
      }
    );
  }

  const loading = ref(false);
  async function archiveTable() {
    const orders = props.orders;

    try {
      loading.value = true;

      const updatedOrders = await TablesService.close(props.table.id);

      ui.showToast(AlertType.Success, 'Mesa finalizada, pedidos archivados');
    } catch (e) {
      ui.serverError(e, 'Error al finalizar la mesa');

      // Refresh the orders in case they were updated serverside
      for (const order of props.orders) {
        ordersHelper.refreshOrder({ order }).catch((err) => {
          console.error(
            `Error refreshing order ${order.id} after table archive error`,
            err
          );
        });
      }
    } finally {
      loading.value = false;
    }
  }

  //#region Search
  const search = computed({
    get: () => props.search ?? null,
    set: (value: string | null) => {
      emit('update:search', value);
    },
  });

  const searchInput = ref<any | null>(null);

  function toggleSearch() {
    if (search.value === null) {
      search.value = '';
      nextTick(() => {
        searchInput.value.focus();
      });
    } else {
      search.value = null;
    }
  }
  //#endregion
</script>

<template>
  <div class="table-header">
    <div class="main-title">
      <div class="title-top">
        <h3>Pedidos mesa {{ table.name }}</h3>
        <v-btn
          v-if="search === null"
          small
          color="primary"
          text
          @click="toggleSearch"
        >
          <v-icon small>mdi-magnify</v-icon>
          <span>Buscar</span>
        </v-btn>
      </div>
      <v-text-field
        ref="searchInput"
        class="search-input"
        v-show="search !== null"
        v-model="search"
        clearable
        dense
        outlined
        rounded
        hide-details
        prepend-inner-icon="mdi-magnify"
        placeholder="Buscar"
        @click:clear="toggleSearch"
        @blur="search?.trim() === '' && toggleSearch()"
      />
    </div>
    <div class="actions">
      <!-- Finalizar, + Pedido -->
      <v-btn
        color="info"
        @click="archiveTable"
        :disabled="!orders.length"
        class="action-btn"
        small
        depressed
        :loading="loading"
      >
        <v-icon small left>mdi-exit-run</v-icon>
        Finalizar
      </v-btn>
      <v-btn
        color="success"
        @click="openNewOrderModal"
        class="action-btn"
        small
        depressed
        :disabled="loading"
      >
        <v-icon small left>mdi-plus</v-icon>
        Pedido
      </v-btn>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .table-header {
    @apply tw-px-2 tw-py-1 tw-flex-none tw-flex tw-items-center tw-justify-start tw-min-h-14 tw-space-x-4;

    .main-title {
      @apply tw-flex-1 tw-flex tw-flex-col tw-items-start;

      .title-top {
        @apply tw-flex tw-items-center tw-space-x-2;
      }

      ::v-deep(.v-input__icon--clear) {
        opacity: 1 !important;
      }
    }

    .actions {
      @apply tw-flex-none tw-flex tw-flex-col-reverse tw-items-end tw-space-y-1 tw-space-y-reverse;
      @apply sm:tw-flex-row sm:tw-space-x-2 sm:tw-space-y-0;

      .action-btn {
        @apply tw-rounded-md;
      }
    }
  }
</style>
